import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { formatCode } from "@/clinical-codes/codeUtils";

export type Icd10Code = Tables<"icd10_codes">;

export type Icd10CodeWithComorbidityInfo = Icd10Code & {
  isMandatoryComorbidity: boolean;
};

export type UseSearchIcd10CodesResult = {
  codes: Icd10CodeWithComorbidityInfo[] | null;
  isLoading: boolean;
  error: Error | null;
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
  getCodeByValue: (code: string) => Promise<Icd10Code | null>;
};

const PAGE_SIZE = 100;

export const useSearchIcd10Codes = (searchQuery: string = ""): UseSearchIcd10CodesResult => {
  const {
    data: codesData,
    error: codesError,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["icd10-codes", searchQuery],
    queryFn: async ({ pageParam }) => {
      let query = supabase.from("icd10_codes").select(
        `
        *,
        icd_10_co_morbidities_codes(
          always_code
        )
      `,
        { count: "exact" },
      );

      if (searchQuery) {
        const formattedCode = formatCode(searchQuery);
        query = query.or(
          `code.ilike.%${formattedCode}%,alt_code.ilike.%${formattedCode}%,description.ilike.%${searchQuery}%`,
        );
      }

      const start = Number(pageParam) * PAGE_SIZE;
      query = query.order("code", { ascending: true }).range(start, start + PAGE_SIZE - 1);

      const { data, error, count } = await query;
      if (error) {
        throw error;
      }

      // Transform the data to include co-morbidity information
      const codesWithComorbidityInfo: Icd10CodeWithComorbidityInfo[] = (data || []).map((code) => ({
        ...code,
        isMandatoryComorbidity: code.icd_10_co_morbidities_codes?.always_code ?? false,
      }));

      return {
        codes: codesWithComorbidityInfo,
        pageParam: Number(pageParam),
        count: count || 0,
      };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.count) return undefined;
      const nextPage = lastPage.pageParam + 1;
      const totalPages = Math.ceil(lastPage.count / PAGE_SIZE);
      return nextPage < totalPages ? nextPage : undefined;
    },
    initialPageParam: 0,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    meta: {
      errorMessage: "Failed to load ICD-10 codes",
    },
  });

  const codeCache = useMemo(() => {
    if (!codesData?.pages) return new Map<string, Icd10CodeWithComorbidityInfo>();
    return new Map(codesData.pages.flatMap((page) => page.codes).map((code) => [code.code.trim().toUpperCase(), code]));
  }, [codesData?.pages]);

  const getCodeByValue = async (codeValue: string): Promise<Icd10Code | null> => {
    const standardizedCode = codeValue.trim().toUpperCase();
    const cachedCode = codeCache.get(standardizedCode);
    if (cachedCode) {
      return cachedCode;
    }
    const { data, error } = await supabase.from("icd10_codes").select("*").eq("code", standardizedCode).maybeSingle();

    if (error) {
      return null;
    }

    return data;
  };

  if (codesError) {
    toast.error("Failed to load ICD-10 codes. Please refresh the page.");
  }

  const fetchNext = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  };

  return {
    codes: codesData?.pages.flatMap((page) => page.codes) ?? [],
    isLoading,
    error: codesError,
    fetchNextPage: fetchNext,
    hasNextPage: hasNextPage ?? false,
    getCodeByValue,
  };
};
